import { Component, OnInit, OnDestroy, inject, ViewChild, HostBinding, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Avatar } from '../../../shared/components/avatar/avatar';
import { SidebarService } from '../../../core/services/sidebar';
import { Auth } from '../../../core/auth/auth';
import { CustomizedSidebarService, CustomizedSidebarConfig } from '../../../core/services/customized-sidebar.service';
import { CustomizedSidebarComponent } from '../../../features/dashboard/components/customized-sidebar/customized-sidebar.component';

interface MenuGroup {
  groupName: string;
  menus: any[];
  isExpanded?: boolean;
}

@Component({
  selector: 'app-sub-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, CustomizedSidebarComponent],
  templateUrl: './sub-sidebar.html',
  styleUrl: './sub-sidebar.scss',
})
export class SubSidebar implements OnInit, OnDestroy {
  @ViewChild('subSidebar') subSidebar!: ElementRef;
  @HostBinding('class.collapsed-host')
  get isHostCollapsed() {
    return (
      this.isCollapsed ||
      (this.activeParentName !== 'Home' && this.subMenus.length === 0 && this.groupedMasters.length === 0)
    );
  }


  private lastParentId: number | null = null;
  private readonly sidebarService = inject(SidebarService);
  private readonly auth = inject(Auth);
  private readonly router = inject(Router);
  private readonly customizedSidebarService = inject(CustomizedSidebarService);

  hoverTop = 0;
  private hoveredItemCenter = 0;
  private hoveredMenuTop = 0;
  activeParentId: number | null = null;
  isHoveredMode = false;
  hoveredParentId: number | null = null;
  selectedParentId: number | null = null;

  activeParentName = '';
  subMenus: any[] = [];
  filteredSubMenus: any[] = [];
  groupedMasters: MenuGroup[] = [];
  filteredGroupedMasters: MenuGroup[] = [];
  searchTerm = '';
  isSearchVisible = false;
  isCollapsed = false;

  // Customized Sidebar Properties
  customizedConfig: CustomizedSidebarConfig | null = null;
  isCustomizeModalOpen = false;
  isCustomSidebarSupported = false;

  private subscription = new Subscription();

  // Keep track of which accordion headers are expanded
  expandedGroups = new Set<string>();

  get loggedUser() {
    return this.auth.currentUser();
  }

  get isMyTeam(): boolean {
    const url = this.router.url.toLowerCase();
    const parent = (this.activeParentName || '').toLowerCase().replace(/[\s-_]+/g, '');
    return url.includes('/myteam') || url.includes('/my-team') || parent.includes('myteam');
  }

  ngOnInit(): void {
    this.isCustomSidebarSupported = this.customizedSidebarService.isRoleSupported(this.loggedUser);

    this.subscription.add(
      this.customizedSidebarService.config$.subscribe((cfg) => {
        this.customizedConfig = cfg;
      })
    );

    this.sidebarService.hoverTop$.subscribe((top) => {
      this.hoveredMenuTop = top;

      if (this.isHoveredMode) {
        this.positionHoverPopover();
      }
    });
    // Default expand Quick Links
    this.expandedGroups.add('Quick Links');


    this.subscription.add(
      this.sidebarService.selectedParentId$.subscribe((id) => {
        this.selectedParentId = id;
        this.updateActiveParentDetails();
      }),
    );

    this.subscription.add(
      this.sidebarService.hoveredParentId$.subscribe((id) => {
        this.hoveredParentId = id;
        this.updateActiveParentDetails();
        if (this.isHoveredMode) {
          this.positionHoverPopover();
        }
      }),
    );

    this.subscription.add(
      this.sidebarService.isHoveredMode$.subscribe((hovered) => {
        this.isHoveredMode = hovered;
        this.updateActiveParentDetails();
        if (this.isHoveredMode) {
          this.positionHoverPopover();
        }
      }),
    );

    this.subscription.add(
      this.sidebarService.menuList$.subscribe(() => {
        this.updateActiveParentDetails();
      }),
    );

    this.subscription.add(
      this.sidebarService.isSubSidebarCollapsed$.subscribe((collapsed) => {
        this.isCollapsed = collapsed;
      }),
    );

    this.subscription.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.checkAndExpandActiveGroups();
        })
    );
  }
  private positionHoverPopover(): void {
    if (!this.isHoveredMode) {
      return;
    }

    // Wait until Angular has finished rendering the new submenu
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.subSidebar) {
          return;
        }

        const panel = this.subSidebar.nativeElement as HTMLElement;

        const panelHeight = panel.offsetHeight;
        const viewportHeight = window.innerHeight;
        const margin = 16;

        // hoveredMenuTop is the TOP of the primary menu item
        let top = this.hoveredMenuTop;

        // Keep the complete submenu inside viewport
        if (top + panelHeight > viewportHeight - margin) {
          top = viewportHeight - panelHeight - margin;
        }

        // Don't allow it above viewport
        if (top < margin) {
          top = margin;
        }

        this.hoverTop = top;
      });
    });
  }
  onMouseEnter(): void {
    this.sidebarService.clearHoverTimeout();
  }
  @HostBinding('class.hovered-host')
  get hoveredHostClass() {
    return this.isHoveredMode;
  }
  onMouseLeave(): void {
    if (this.isHoveredMode) {
      this.sidebarService.startHoverTimeout(() => {
        const isCollapsedByUser = this.sidebarService.getCollapsedByUser();
        if (isCollapsedByUser) {
          this.sidebarService.setSubSidebarCollapsed(true);
          this.sidebarService.setIsHoveredMode(false);
          this.sidebarService.setHoveredParentId(null);
        } else {
          this.sidebarService.setIsHoveredMode(false);
          this.sidebarService.setHoveredParentId(null);
          this.sidebarService.setSubSidebarCollapsed(this.sidebarService.getCollapsedByUser());
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateActiveParentDetails(): void {
    const allMenus = this.sidebarService.getMenuList();
    const parentId = this.isHoveredMode ? this.hoveredParentId : this.selectedParentId;
    this.activeParentId = parentId;

    if (this.lastParentId !== parentId) {
      this.searchTerm = '';
      this.isSearchVisible = false;
      this.lastParentId = parentId;
    }

    if (!parentId || allMenus.length === 0) {
      this.subMenus = [];
      this.filteredSubMenus = [];
      this.groupedMasters = [];
      this.filteredGroupedMasters = [];
      this.activeParentName = '';
      return;
    }

    const parentMenu = allMenus.find((m) => m.id === parentId);
    if (parentMenu) {
      this.activeParentName = parentMenu.name;
    } else if (parentId === 99999) {
      this.activeParentName = 'Home';
    } else {
      this.activeParentName = '';
    }


    // 1. Get all Tier 2 submenus directly under this Tier 1 parent
    const tier2Menus: any[] = allMenus
      .filter((m) => m.parentID === parentId && m.id !== m.parentID && m.can_view)
      .map((m) => ({ ...m, link: this.normalizeMenuLink(m.link) }));

    if (this.isMyTeam) {
      // 2. Identify all Tier 3 child menus belonging to these Tier 2 parents (My Team only)
      const tier2Ids = new Set(tier2Menus.map((m) => m.id));
      const allTier3Menus: any[] = allMenus
        .filter((m) => m.parentID && tier2Ids.has(m.parentID) && m.can_view)
        .map((m) => ({ ...m, link: this.normalizeMenuLink(m.link) }));

      // 3. Attach children to Tier 2 items and detect active route for auto-expand
      const currentUrl = this.router.url.split('?')[0].split('#')[0];
      this.subMenus = tier2Menus.map((parent) => {
        const children = allTier3Menus
          .filter((child) => child.parentID === parent.id)
          .sort((a, b) => (a.order ?? a.sort_order ?? 0) - (b.order ?? b.sort_order ?? 0));

        const hasChildren = children.length > 0;
        if (hasChildren) {
          const isChildActive = children.some(
            (c) => c.link && currentUrl.startsWith(c.link)
          );
          if (isChildActive) {
            this.expandedGroups.add(parent.name);
          }
        }

        return {
          ...parent,
          hasChildren,
          children,
        };
      });
    } else {
      this.subMenus = tier2Menus;
    }

    const hasSubGroups = this.subMenus.some((m) => m.sub_group && m.sub_group.trim() !== '');

    if (hasSubGroups) {
      this.buildMastersGroups(this.subMenus);
    } else {
      this.groupedMasters = [];
      this.filteredGroupedMasters = [];
    }

    this.filterSubMenus();
    if (this.isHoveredMode) {
      this.positionHoverPopover();
    }
  }

  private normalizeMenuLink(link: string): string {
    if (!link) return '';
    if (
      link === '/app/master/leave-master' ||
      link === '/app/master/leave-master-main' ||
      link === '/master/leave-master'
    ) {
      return '/admin-master/absence-code';
    }
    if (link === '/app/master/travel-exp-list/request') {
      return '/requests/expense';
    }
    if (link === '/app/master/travel-exp-list/approve') {
      return '/requests/approve-expense';
    }
    if (
      link === '/app/company/pay/list-break' ||
      link === '/app/master/break' ||
      link === '/master/break' ||
      link === '/break'
    ) {
      return '/admin-master/break';
    }
    if (
      link === '/app/master/work-shift-list' ||
      link === '/app/master/work-shift' ||
      link === '/master/work-shift-list' ||
      link === '/master/work-shift' ||
      link === '/work-shift-list' ||
      link === '/app/master/shift-master' ||
      link === '/master/shift-master' ||
      link === '/shift-master'
    ) {
      return '/admin-master/work-shift';
    }
    return link;
  }

  private checkAndExpandActiveGroups(): void {
    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    for (const item of this.subMenus) {
      if (item.hasChildren && item.children) {
        const isChildActive = item.children.some(
          (c: any) => c.link && currentUrl.startsWith(c.link)
        );
        if (isChildActive) {
          this.expandedGroups.add(item.name);
        }
      }
    }
  }

  private buildMastersGroups(mastersSubMenus: any[]): void {
    const groupsMap = new Map<string, any[]>();

    mastersSubMenus.forEach((menu) => {
      const groupName = menu.sub_group || this.activeParentName;

      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
      }
      groupsMap.get(groupName)!.push(menu);
    });

    // Sort group names alphabetically to maintain consistent ordering
    const sortedGroupNames = Array.from(groupsMap.keys()).sort();

    this.groupedMasters = sortedGroupNames.map((groupName) => {
      // Default-expand group
      this.expandedGroups.add(groupName);
      return {
        groupName,
        menus: groupsMap.get(groupName) || [],
      };
    });
  }

  filterSubMenus(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredSubMenus = [...this.subMenus];
      this.filteredGroupedMasters = [...this.groupedMasters];
      return;
    }

    this.filteredSubMenus = this.subMenus
      .map((item) => {
        const matchesParent = item.name && item.name.toLowerCase().includes(term);
        const matchingChildren = (item.children || []).filter((c: any) =>
          c.name && c.name.toLowerCase().includes(term)
        );

        if (matchesParent || matchingChildren.length > 0) {
          if (matchingChildren.length > 0) {
            this.expandedGroups.add(item.name);
          }
          return {
            ...item,
            children: matchingChildren.length > 0 ? matchingChildren : item.children,
          };
        }
        return null;
      })
      .filter((item): item is any => item !== null);

    this.filteredGroupedMasters = this.groupedMasters
      .map((g) => ({
        ...g,
        menus: g.menus.filter((m) => m.name.toLowerCase().includes(term)),
      }))
      .filter((g) => g.menus.length > 0);
  }

  toggleGroup(groupName: string): void {
    if (this.expandedGroups.has(groupName)) {
      this.expandedGroups.delete(groupName);
    } else {
      this.expandedGroups.add(groupName);
    }
  }

  isGroupExpanded(groupName: string): boolean {
    return this.expandedGroups.has(groupName);
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
    if (!this.isSearchVisible) {
      this.searchTerm = '';
      this.filterSubMenus();
    }
  }

  collapseSidebar(): void {
    this.sidebarService.setCollapsedByUser(true);
    this.sidebarService.setSubSidebarCollapsed(true);
  }

  setMenuId(id: number): void {
    this.auth.selectedMenuID = id;
    const allMenus = this.sidebarService.getMenuList();
    const matched = allMenus.find((m: any) => m.id === id);
    if (matched) {
      localStorage.setItem('activeMenuName', matched.name);
      localStorage.setItem('activeMenu', JSON.stringify(matched));
    }
    if (this.isHoveredMode && this.hoveredParentId) {
      this.sidebarService.setSelectedParentId(this.hoveredParentId);
      this.sidebarService.setIsHoveredMode(false);
      this.sidebarService.setHoveredParentId(null);
      this.sidebarService.setSubSidebarCollapsed(this.sidebarService.getCollapsedByUser());
    }
  }

  navigateToParentModule(moduleName: string): void {
    const allMenus = this.sidebarService.getMenuList();
    let targetParentName = '';

    switch (moduleName) {
      case 'Attendance':
        targetParentName = 'Time';
        break;
      case 'Payrolls':
        targetParentName = 'Payroll';
        break;
      case 'Organisation':
        targetParentName = 'Org';
        break;
      case 'Hiring':
        targetParentName = 'Employees';
        break;
      case 'Helpdesk':
        targetParentName = 'Comm';
        break;
      default:
        targetParentName = moduleName;
    }

    const parent = allMenus.find(
      (m) =>
        (!m.parentID || m.parentID === m.id) &&
        m.name.toLowerCase().includes(targetParentName.toLowerCase()),
    );

    if (parent) {
      this.sidebarService.setSelectedParentId(parent.id);
      const subMenus = allMenus.filter(
        (m: any) => m.parentID === parent.id && m.id !== m.parentID && m.can_view,
      );
      if (subMenus.length > 0) {
        const firstSub = subMenus.sort((a, b) => a.order - b.order)[0];
        this.auth.selectedMenuID = firstSub.id;
        this.router.navigate([firstSub.link]);
      }
    }
  }

  getCompanyInitials(item: any): string {
    return item?.company_name ? item.company_name.charAt(0).toUpperCase() : 'C';
  }

  openCustomizeSidebarModal(): void {
    this.isCustomizeModalOpen = true;
  }

  onCustomizedSidebarSave(newConfig: CustomizedSidebarConfig): void {
    this.customizedConfig = newConfig;
  }

  isExternalUrl(url?: string): boolean {
    return !!url && /^https?:\/\//i.test(url);
  }

  setCustomMenuId(item: any): void {
    const allMenus = this.sidebarService.getMenuList() || [];
    const menuId = item?.menu_id || Number(item?.id);
    const matched = allMenus.find((m: any) => m.id === menuId || (item?.link && m.link === item.link));

    if (matched) {
      this.auth.selectedMenuID = matched.id;
      localStorage.setItem('activeMenuName', matched.name);
      localStorage.setItem('activeMenu', JSON.stringify(matched));
    } else if (item?.name) {
      if (menuId && !isNaN(menuId)) this.auth.selectedMenuID = menuId;
      localStorage.setItem('activeMenuName', item.name);
    }

    if (this.isHoveredMode && this.hoveredParentId) {
      this.sidebarService.setSelectedParentId(this.hoveredParentId);
      this.sidebarService.setIsHoveredMode(false);
      this.sidebarService.setHoveredParentId(null);
      this.sidebarService.setSubSidebarCollapsed(this.sidebarService.getCollapsedByUser());
    }
  }
}
